import React from 'react';
import { AccountDetailHeader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-header';
import { AccountDetailHeaderSkeleton } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-header-skeleton';
import { AccountDetailItemsOfInterest } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-items-of-interest';
import {
  AccountDetailMetric,
  MetricsTimeRange,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-detail-metrics-loader';
import { AccountDetailMetrics } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-metrics';
import { AccountDetailRestaurant } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-details-loader';
import { AccountDetailSalesAlerts } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-sales-alerts';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import { ActivityNotesDesktop } from '@/experiences/sales-portal/components/activity-notes/activity-notes-desktop';
import { ActivityNotesDesktopSkeleton } from '@/experiences/sales-portal/components/activity-notes/activity-notes-desktop-skeleton';
import { ActivityNotesMobile } from '@/experiences/sales-portal/components/activity-notes/activity-notes-mobile';
import { ActivityNotesMobileSkeleton } from '@/experiences/sales-portal/components/activity-notes/activity-notes-mobile-skeleton';
import { AddressInputType } from '@domain/addresses/types/types';
import { ApolloError } from '@apollo/client';
import { CompactMap } from '@/experiences/sales-portal/components/map/compact-map';
import { CompactMapSkeleton } from '@/experiences/sales-portal/components/map/compact-map-skeleton';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import { ItemOfInterest } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-items-of-interest-loader';
import { Map } from '@/experiences/sales-portal/components/map/map';
import { MapMarkerData } from '@/experiences/sales-portal/components/map/types';
import { MapSkeleton } from '@/experiences/sales-portal/components/map/map-skeleton';
import { SalesAlert } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/types';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { View } from 'react-native';

interface AccountDetailContentProps {
  accountId: string | null;
  activityNotes: ActivityNote[];
  activityNotesHasError: boolean;
  additionalHeaderContent: React.ReactNode;
  address: AddressInputType | null;
  coordinate?: Coordinate;
  fetchingMoreActivityNotes: boolean;
  isProspect: boolean;
  itemsOfInterest: ItemOfInterest[] | null;
  itemsOfInterestError?: ApolloError;
  loading: boolean;
  loadingActivityNotes: boolean;
  markers?: MapMarkerData[];
  metrics: AccountDetailMetric[] | null;
  metricsError?: ApolloError;
  onRetryActivityNotes: () => void;
  onTimeRangeChange: (timeRange: MetricsTimeRange) => void;
  restaurant?: AccountDetailRestaurant;
  salesAlerts: SalesAlert[];
  selectedTimeRange: MetricsTimeRange;
  sidebarContent: React.ReactNode;
}

export const AccountDetailContent: React.FC<AccountDetailContentProps> = ({
  accountId,
  activityNotes,
  activityNotesHasError,
  additionalHeaderContent,
  address,
  coordinate,
  isProspect,
  itemsOfInterest,
  itemsOfInterestError,
  fetchingMoreActivityNotes,
  onRetryActivityNotes,
  loading,
  loadingActivityNotes,
  markers,
  metrics,
  metricsError,
  onTimeRangeChange,
  restaurant,
  salesAlerts,
  selectedTimeRange,
  sidebarContent,
}) => {
  const [styles] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();

  const contentColumnStyle = [
    styles.contentColumn,
    isMobileBottomNavLayout ? styles.fullWidthColumn : null,
  ];

  if (!loading && !restaurant) {
    // TODO(DSR-3633): Add error state
    return null;
  }

  const header =
    loading || !restaurant ? (
      <AccountDetailHeaderSkeleton />
    ) : (
      <AccountDetailHeader
        accountId={accountId}
        address={address}
        name={restaurant.name}
      />
    );

  const headerBlock = (
    <>
      {header}
      {additionalHeaderContent}
    </>
  );

  const showMetrics = !metricsError;
  const showSalesAlerts = salesAlerts.length > 0;
  const showItemsOfInterest = !isProspect && !itemsOfInterestError;
  const showActivityNotesSkeleton =
    loadingActivityNotes && activityNotes.length === 0;

  const sectionSpacer = isMobileBottomNavLayout ? null : (
    <View style={styles.sectionSpacer} />
  );

  return (
    <>
      {isMobileBottomNavLayout ? null : loading ? (
        <MapSkeleton />
      ) : (
        <Map coordinate={coordinate} markers={markers} />
      )}
      <View style={styles.mainLayout}>
        <View style={contentColumnStyle}>
          {isMobileBottomNavLayout ? (
            <View style={styles.heroRow}>
              {loading ? (
                <CompactMapSkeleton />
              ) : (
                <CompactMap coordinate={coordinate} markers={markers} />
              )}
              <View style={styles.heroColumn}>{headerBlock}</View>
            </View>
          ) : (
            headerBlock
          )}
          {/* TODO(DSR-3633): Add error state */}
          {showMetrics ? (
            <AccountDetailMetrics
              metrics={metrics}
              onTimeRangeChange={onTimeRangeChange}
              selectedTimeRange={selectedTimeRange}
            />
          ) : null}
          {showSalesAlerts ? (
            <>
              {showMetrics ? sectionSpacer : null}
              <AccountDetailSalesAlerts salesAlerts={salesAlerts} />
            </>
          ) : null}
          {/* TODO(DSR-3633): Add error state */}
          {showItemsOfInterest ? (
            <>
              {showMetrics || showSalesAlerts ? sectionSpacer : null}
              <AccountDetailItemsOfInterest items={itemsOfInterest} />
            </>
          ) : null}
          {showMetrics || showSalesAlerts || showItemsOfInterest
            ? sectionSpacer
            : null}
          {showActivityNotesSkeleton ? (
            isMobileBottomNavLayout ? (
              <ActivityNotesMobileSkeleton />
            ) : (
              <ActivityNotesDesktopSkeleton />
            )
          ) : isMobileBottomNavLayout ? (
            <ActivityNotesMobile
              activityNotes={activityNotes}
              hasError={activityNotesHasError}
              loading={fetchingMoreActivityNotes}
              onRetry={onRetryActivityNotes}
            />
          ) : (
            <ActivityNotesDesktop
              activityNotes={activityNotes}
              hasError={activityNotesHasError}
              loading={fetchingMoreActivityNotes}
              onRetry={onRetryActivityNotes}
            />
          )}
        </View>
        {isMobileBottomNavLayout ? null : (
          <View style={styles.sidebarColumn}>{sidebarContent}</View>
        )}
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  /* Note: The body of account details follow a 10 column grid layout
     The left column is 6 columns wide and the right column is 3 columns wide
    with a gap of 1 column between them */
  contentColumn: {
    gap: theme.sk.spacing.spaceXsmall,
    overflow: 'visible',
    paddingBottom: theme.sk.spacing.spaceMedium,
    width: '60%',
  },
  fullWidthColumn: {
    gap: theme.sk.spacing.spaceXlarge,
    width: '100%',
  },
  heroColumn: {
    flex: 1,
    // Lets the name and address ellipsize instead of squeezing the map.
    minWidth: 0,
  },
  heroRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  mainLayout: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.sk.spacing.spaceMedium,
  },
  sectionSpacer: {
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    marginVertical: theme.sk.spacing.spaceSMedium,
  },
  sidebarColumn: {
    gap: theme.sk.spacing.spaceSmall,
    width: '30%',
  },
}));
